import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { skipLoader } from '@rsm/shared/loader';
import { FilterInvite } from 'src/app/pages/managers/filter-invite';
import { environment } from 'src/environments/environment';

export interface UserLicenseStatus {
  /** PENDING | IN_USE | EXPIRED | NONE */
  status: string;
  expiresAt?: string | null;
  /** PACOTE (licença do plano contratado) | AVULSA (comprada avulsa) | null quando NONE. */
  licenseType?: 'PACOTE' | 'AVULSA' | null;
  autoRenew?: boolean | null;
  /** Estoque de licenças de pacote disponíveis na empresa do agente. */
  packageStock?: number | null;
  /** Estoque de licenças avulsas disponíveis na empresa do agente. */
  standaloneStock?: number | null;
}

export interface AgentLicenseRenewalSummary {
  /** false para empresas Politics — data de expiração é contratual fixa, não configurável aqui. */
  applicable: boolean;
  totalEligible: number;
  renewingQuantity: number;
  cancelledQuantity: number;
}

export interface ActionStats {
  accepted: number;
  refused: number;
  concluded: number;
}

export interface OnboardingStats {
  adhered: number;
  pending: number;
  completed: number;
  inProgress: number;
  instagram: number;
  facebook: number;
  threads: number;
}

export interface OnboardingCustomAction {
  sectionId: string;
  title: string;
  points: number;
  companyContextId: string;
  companyName: string | null;
  updatedAt: string | null;
}

export interface FieldLeaderStats {
  teamId: string;
  teamName: string;
  coordinatorId: string;
  coordinatorName: string;
  memberCount: number;
  concludedCount: number;
  progress: number;
}

export interface InAppNotification {
  id: string;
  missionId: string;
  type: string;
  payloadJson: string | null;
  createdAt: string;
}

export interface AdminNotificationEvent {
  id: string;
  eventType: string;
  originSponsorContextId?: string | null;
  originSponsorName?: string | null;
  title: string;
  body: string;
  contextType: string | null;
  contextId: string | null;
  contextUrl: string | null;
  read: boolean;
  reactionCount: number;
  createdAt: string;
}

export interface AdminNotificationPage {
  content: AdminNotificationEvent[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface NotificationPreference {
  role: string;
  eventType: string;
  enabled: boolean;
}

export interface UsersMapRegion {
  regionLabel: string;
  count: number;
  precisionLevel: 'CITY' | 'STATE' | 'COUNTRY' | 'NOT_INFORMED' | string;
  privacyApplied: boolean;
  city?: string;
  state?: string;
}

export interface UsersMapSummary {
  totalAgents: number;
  groupedAgents: number;
  privacyThreshold: number;
  groups: UsersMapRegion[];
}

export interface AdministrativeProfileGrantRequest {
  companyId: string | null;
  profileId: string | null;
  profileIdentify?: number | null;
  profileName?: string | null;
  agentRole: 'COORDENADOR' | 'COMUM' | 'ANJO' | string;
  agentRoleLabel?: string | null;
}

/** Troca somente o perfil de agente (Coordenador / Agente Pleno / Anjo). */
export interface AgentRoleUpdateRequest {
  companyId: string | null;
  agentRole: 'COORDENADOR' | 'COMUM' | 'ANJO' | string;
  agentRoleLabel?: string | null;
}

export interface AdministrativeProfileGrantResponse {
  companyId?: string | null;
  profileId?: string | null;
  profileDescription?: string | null;
  statusId?: string | null;
  statusDescription?: string | null;
  inviteId?: string | null;
  agentLevelDescription?: string | null;
}

export interface TermsOfUse {
  id: string;
  title: string;
  documentType: TermsOfUseDocumentType;
  content: string;
  observation?: string | null;
  active: boolean;
  accepted?: boolean | null;
  createdBy?: string | null;
  createdByName?: string | null;
  updatedBy?: string | null;
  updatedByName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  acceptedAt?: string | null;
  optedOut?: boolean | null;
  optedOutAt?: string | null;
  optOutReason?: string | null;
}

export type TermsOfUseDocumentType =
  | 'CLIENT_RSM_CAMPAIGNS'
  | 'CLIENT_RSM_DEDICATED'
  | 'CLIENT_RSM_POLITICS'
  | 'USER_COMMERCIAL_APP'
  | 'USER_VOLUNTEER_POLITICS_APP'
  | 'PRIVACY_POLICY_APP';

export interface TermsOfUseRequest {
  title: string;
  documentType: TermsOfUseDocumentType;
  content: string;
  observation?: string | null;
}

export interface TermsOfUseAgentLog {
  id: string;
  termsOfUseId: string;
  termsOfUseTitle?: string | null;
  documentType?: TermsOfUseDocumentType | null;
  agentId?: string | null;
  userId: string;
  agentName?: string | null;
  agentEmail?: string | null;
  agentPhoneNumber?: string | null;
  accepted?: boolean | null;
  acceptedAt?: string | null;
  optedOut?: boolean | null;
  optedOutAt?: string | null;
  optOutReason?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AgenteClientService {
  constructor(private http: HttpClient) { }

  getAgent(userId: string | number) {
    return this.http.get(`${environment.agent.url}/agent/user/${userId}`, {
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
  }

  /** Índice de engajamento do agente (nota 0–5) para a chip do detalhe do usuário. */
  getAgentEngagement(userId: string): Observable<any> {
    return this.http.get<any>(`${environment.agent.url}/agent/user/${userId}/engagement`);
  }

  /** Engajamento 0–100 por tipo de ação (gráfico "Engajamento em ações"); inclui tipos zerados. */
  getAgentEngagementByActionType(userId: string): Observable<any> {
    return this.http.get<any>(`${environment.agent.url}/agent/user/${userId}/engagement/by-action-type`);
  }

  /**
   * Árvore de indicações paginada (modal "Indicações"): convidados diretos do
   * nó `rootUserId`, com estrela (cadastro completo), contagem de convidados e
   * pontos de cada um.
   */
  getNetworkTree(rootUserId: string, page: number, size: number): Observable<any> {
    return this.http.get<any>(
      `${environment.agent.url}/agent/app/network/tree?rootUserId=${rootUserId}&page=${page}&size=${size}`
    );
  }

  /** KPIs do card de perfil (chats, ações participadas, convites enviados, último acesso, tempo ativo). */
  getAgentProfileStats(userId: string): Observable<any> {
    return this.http.get<any>(`${environment.agent.url}/agent/user/${userId}/profile-stats`);
  }

  /** Card "Resumo de Desempenho": pontos, posição no ranking, quem convidou e times. */
  getAgentPerformanceSummary(userId: string): Observable<any> {
    return this.http.get<any>(`${environment.agent.url}/agent/user/${userId}/performance-summary`);
  }

  /** Série do gráfico "Total de ações cumpridas": anos com dado + contagem mensal. */
  getCompletedActionsSummary(userId: string): Observable<any> {
    return this.http.get<any>(`${environment.agent.url}/agent/user/${userId}/completed-actions/summary`);
  }

  /** "Histórico de ações cumpridas", paginado no padrão do sistema (Page + page/size). */
  getCompletedActionsHistory(userId: string, page: number, size: number): Observable<any> {
    return this.http.get<any>(
      `${environment.agent.url}/agent/user/${userId}/completed-actions?page=${page}&size=${size}`
    );
  }

  /**
   * Seguidores do agente em cada rede social conectada (Instagram/Threads/
   * Facebook), com link do perfil público. Path = id do AGENTE (co_agent).
   */
  getSocialFollowers(agentId: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.agent.url}/social/${agentId}/followers`
    );
  }

  getAgentesSegmentacao(filtro: any = ''): Observable<any> {
    return this.http.get<any>(
      `${environment.agent.url}/agent/segmentation` + filtro
    );
  }

  getLinkedBaseBreakdown(filtro: any = ''): Observable<any> {
    return this.http.get<any>(
      `${environment.agent.url}/agent/segmentation/linked-bases` + filtro
    );
  }

  getCompanyFieldTemplate(contextId: string): Observable<any> {
    return this.http.get<any>(
      `${environment.agent.url}/agent/user/company/${contextId}/field-template`
    );
  }

  getAllAgentsByCompany(contextId: any) {
    return this.http.get(`${environment.agent.url}/agent/company/${contextId}`);
  }

  getAllAgentsByFilter(contextId: any, filter: FilterInvite) {
    return this.http.get(
      `${environment.agent.url}/agent/user/company/${contextId}/filter`,
      {
        params: this.buildHttpParams(filter),
      }
    );
  }

  getCoordinators(): Observable<{ userId: string; name: string }[]> {
    return this.http.get<{ userId: string; name: string }[]>(
      `${environment.agent.url}/agent/user/coordinators`
    );
  }

  getUsersMapSummary(contextId: any, filter: FilterInvite): Observable<UsersMapSummary> {
    return this.http.get<UsersMapSummary>(
      `${environment.agent.url}/agent/user/company/${contextId}/map/summary`,
      {
        params: this.buildHttpParams(filter),
      }
    );
  }

  public getWalletBalance(userId: string) {
    return this.http.get<any>(`${environment.agent.url}/agent/wallet-by-user/${userId}`);
  }

  getActionFieldLeaders(actionId: string): Observable<FieldLeaderStats[]> {
    return this.http.get<FieldLeaderStats[]>(`${environment.agent.url}/agent_mission/stats/${actionId}/leaders`);
  }

  getActionStats(actionId: string): Observable<ActionStats> {
    return this.http.get<ActionStats>(`${environment.agent.url}/agent_mission/stats/${actionId}`);
  }

  getOnboardingStats(contextId?: string): Observable<OnboardingStats> {
    const url = `${environment.agent.url}/agent_mission/onboarding-stats`;
    return this.http.get<OnboardingStats>(
      contextId ? `${url}?contextId=${contextId}` : url
    );
  }

  getOnboardingCustomActions(contextId?: string): Observable<OnboardingCustomAction[]> {
    const url = `${environment.agent.url}/agent_mission/onboarding/custom-actions`;
    return this.http.get<OnboardingCustomAction[]>(
      contextId ? `${url}?contextId=${contextId}` : url
    );
  }

  getAllUsersByFilter(contextId: any, filter: FilterInvite) {
    return this.http.get(
      `${environment.agent.url}/agent/user/company/${contextId}/filter`,
      {
        params: this.buildHttpParams(filter),
      }
    );
  }

  getAllInvitesByFilter(contextId: any, filter: FilterInvite) {
    return this.http.get(
      `${environment.agent.url}/agent/invited/company/${contextId}/filter`,
      {
        params: this.buildHttpParams(filter),
      }
    );
  }

  private buildHttpParams(filter?: FilterInvite): HttpParams {
    let params = new HttpParams();

    if (!filter) {
      return params;
    }

    Object.entries(filter).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      const normalizedValue = `${value}`.trim();
      if (!normalizedValue || normalizedValue === 'undefined' || normalizedValue === 'null') {
        return;
      }

      params = params.set(key, normalizedValue);
    });

    return params;
  }

  createUser(contextId: string, payload: any): Observable<any> {
    return this.http.post(
      `${environment.agent.url}/agent/user/company/${contextId}`,
      payload
    );
  }

  updateAgent(payload: any): Observable<any> {
    return this.http.put(`${environment.agent.url}/agent`, payload);
  }

  getUserCustomSections(userId: string): Observable<any> {
    return this.http.get(
      `${environment.agent.url}/agent/user/${userId}/custom-sections`,
      { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }
    );
  }

  updateUserCustomSections(userId: string, payload: any): Observable<any> {
    return this.http.put(
      `${environment.agent.url}/agent/user/${userId}/custom-sections`,
      payload
    );
  }

  grantAdministrativeProfile(userId: string | number, payload: AdministrativeProfileGrantRequest): Observable<AdministrativeProfileGrantResponse> {
    return this.http.post<AdministrativeProfileGrantResponse>(
      `${environment.agent.url}/agent/user/${userId}/administrative-profile`,
      payload
    );
  }

  /**
   * Aplica apenas o perfil de agente na empresa logada — não dispara convite
   * administrativo nem mexe no perfil de gestor.
   */
  updateAgentRole(userId: string | number, payload: AgentRoleUpdateRequest): Observable<AdministrativeProfileGrantResponse> {
    return this.http.put<AdministrativeProfileGrantResponse>(
      `${environment.agent.url}/agent/user/${userId}/agent-role`,
      payload
    );
  }

  /**
   * Admin solicita que o agente refaça o KYC. O agent-api reseta a missão de KYC
   * local e propaga o reset ao mission-api (que valida o perfil de administrador).
   */
  requestKycRedo(userId: string | number): Observable<any> {
    return this.http.post<any>(
      `${environment.agent.url}/kyc/${userId}/reset`,
      {}
    );
  }

  deleteUser(userId: string | number): Observable<boolean> {
    return this.http.delete<boolean>(
      `${environment.agent.url}/agent/user/${userId}`
    );
  }

  getPendingInAppNotifications(): Observable<InAppNotification[]> {
    return this.http.get<InAppNotification[]>(
      `${environment.agent.url}/notifications/in-app/pending`
    );
  }

  validHasEmail(userId: any): Observable<any> {
    return this.http.get<any>(
      `${environment.agent.url}/agent/valid-email/${userId}`
    );
  }

  generateTokenEmail(userId: string, email: string) {
    return this.http.get(
      `${environment.agent.url}/agent/generate-token-email`,
      {
        params: {
          userId,
          email
        },
        responseType: 'text'
      }
    );
  }

  markInAppNotificationRead(id: string): Observable<void> {
    return this.http.post<void>(
      `${environment.agent.url}/notifications/in-app/${id}/read`,
      {}
    );
  }

  validTokenEmail(token: string): Observable<any> {
    return this.http.post<any>(
      `${environment.agent.url}/agent/valid-token-email?token=${token}`,
      {}
    );
  }

  createInAppNotification(payload: {
    type: string;
    targetUserIds: string[];
    payloadJson?: string;
  }): Observable<void> {
    return this.http.post<void>(
      `${environment.agent.url}/notifications/in-app`,
      payload
    );
  }

  // ── Admin Notifications ──────────────────────────────────────────────────

  /**
   * @param options `silent: true` no polling de segundo plano, para a lista se
   * atualizar sozinha sem bloquear a tela com o overlay de carregamento.
   */
  getAdminNotifications(
    params?: { page?: number; size?: number; read?: boolean },
    options?: { silent?: boolean }
  ): Observable<AdminNotificationPage> {
    let httpParams = new HttpParams()
      .set('page', String(params?.page ?? 0))
      .set('size', String(params?.size ?? 20));
    if (params?.read !== undefined) {
      httpParams = httpParams.set('read', String(params.read));
    }
    return this.http.get<AdminNotificationPage>(
      `${environment.agent.url}/admin-notifications`,
      options?.silent
        ? { params: httpParams, context: skipLoader() }
        : { params: httpParams }
    );
  }

  readonly notificationRead$ = new Subject<void>();

  markAdminNotificationRead(id: string): Observable<void> {
    return this.http.post<void>(
      `${environment.agent.url}/admin-notifications/${id}/read`,
      {}
    ).pipe(tap(() => this.notificationRead$.next()));
  }

  /**
   * @param options `silent: true` no polling do badge de não lidas — ele roda a
   * cada 30s em toda tela e não pode disparar o overlay de carregamento.
   */
  getAdminUnreadCount(options?: { silent?: boolean }): Observable<{ count: number }> {
    const headers = { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' };

    return this.http.get<{ count: number }>(
      `${environment.agent.url}/admin-notifications/unread-count`,
      options?.silent ? { headers, context: skipLoader() } : { headers }
    );
  }

  getNotificationPreferences(): Observable<NotificationPreference[]> {
    return this.http.get<NotificationPreference[]>(
      `${environment.agent.url}/management/notifications`
    );
  }

  updateNotificationPreference(role: string, eventType: string, enabled: boolean): Observable<NotificationPreference> {
    return this.http.put<NotificationPreference>(
      `${environment.agent.url}/management/notifications/${role}/${eventType}`,
      { enabled }
    );
  }

  getTermsOfUse(documentType?: TermsOfUseDocumentType): Observable<TermsOfUse[]> {
    const options = documentType
      ? { params: new HttpParams().set('documentType', documentType) }
      : {};
    return this.http.get<TermsOfUse[]>(`${environment.agent.url}/terms-of-use`, options);
  }

  getTermsOfUseLogs(params?: {
    documentType?: TermsOfUseDocumentType;
    termsOfUseId?: string;
    optedOut?: boolean;
  }): Observable<TermsOfUseAgentLog[]> {
    let httpParams = new HttpParams();
    if (params?.documentType) {
      httpParams = httpParams.set('documentType', params.documentType);
    }
    if (params?.termsOfUseId) {
      httpParams = httpParams.set('termsOfUseId', params.termsOfUseId);
    }
    if (params?.optedOut !== undefined) {
      httpParams = httpParams.set('optedOut', String(params.optedOut));
    }
    return this.http.get<TermsOfUseAgentLog[]>(`${environment.agent.url}/terms-of-use/logs`, { params: httpParams });
  }

  getActiveTermsOfUse(documentType: TermsOfUseDocumentType = 'USER_COMMERCIAL_APP'): Observable<TermsOfUse> {
    return this.http.get<TermsOfUse>(`${environment.agent.url}/terms-of-use/active/${documentType}`);
  }

  getPendingAppTermsOfUse(): Observable<TermsOfUse[]> {
    return this.http.get<TermsOfUse[]>(`${environment.agent.url}/terms-of-use/app/pending`);
  }

  createTermsOfUse(payload: TermsOfUseRequest): Observable<TermsOfUse> {
    return this.http.post<TermsOfUse>(`${environment.agent.url}/terms-of-use`, payload);
  }

  updateTermsOfUseStatus(id: string, active: boolean): Observable<TermsOfUse> {
    return this.http.patch<TermsOfUse>(
      `${environment.agent.url}/terms-of-use/${id}/status`,
      { active }
    );
  }

  acceptActiveTermsOfUse(documentType: TermsOfUseDocumentType = 'USER_COMMERCIAL_APP'): Observable<TermsOfUse> {
    return this.http.post<TermsOfUse>(`${environment.agent.url}/terms-of-use/active/${documentType}/accept`, {});
  }

  getUserLicenseStatus(userId: string | number): Observable<UserLicenseStatus> {
    return this.http.get<UserLicenseStatus>(
      `${environment.agent.url}/agent/license-status/user/${userId}`
    );
  }

  /**
   * Aplica (ou troca) a licença de um usuário. Sem `origin`, o back-end aplica
   * preferindo PACOTE (PLAN) e cai para AVULSA (EXTRA) se não houver estoque.
   * Com `origin`, troca para o tipo pedido devolvendo a atual ao estoque.
   */
  applyUserLicense(userId: string | number, origin?: 'PLAN' | 'EXTRA'): Observable<UserLicenseStatus> {
    const query = origin ? `?origin=${origin}` : '';
    return this.http.post<UserLicenseStatus>(
      `${environment.agent.url}/agent/user/${userId}/license${query}`,
      {}
    );
  }

  /** Resumo do controle de renovação por quantidade da empresa (contexto atual). */
  getLicenseRenewalSummary(contextId: string): Observable<AgentLicenseRenewalSummary> {
    return this.http.get<AgentLicenseRenewalSummary>(
      `${environment.agent.url}/agent-licenses/company/${contextId}/renewal-summary`
    );
  }

  /**
   * Cancela a renovação automática de mais `cancelQuantity` licenças — ação
   * cumulativa e irreversível (uma licença cancelada nunca volta a renovar
   * sozinha, só comprando uma nova).
   */
  cancelLicenseRenewal(contextId: string, cancelQuantity: number): Observable<AgentLicenseRenewalSummary> {
    return this.http.post<AgentLicenseRenewalSummary>(
      `${environment.agent.url}/agent-licenses/company/${contextId}/renewal-cancel-quantity`,
      { cancelQuantity }
    );
  }

}
